import { CommonModule } from '@angular/common';
import { Component, effect, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LegalPersonValidatedFormComponent } from '@app/components/legal-person-validated-form/legal-person-validated-form.component';
import { getLegalEntitiyData } from '@app/components/legal-person-validated-form/legal-person-validated-form.helper';
import {
  DocumentUploadFormGroup,
  getDocumentUploadFormGroup,
} from '@app/components/registration/document-upload-form/document-upload-form.helper';
import {
  FileUploadControl,
  LegalEntityFormGroup,
  PersonalDataFormGroup,
} from '@app/models/form.models';
import { AccountService } from '@app/services/account.service';
import { VeriffService } from '@app/services/veriff.service';
import { FileData } from '@app/services/file/upload.service';
import { LegalPersonService } from '@app/services/profile/legal-person.service';
import { AuthStore } from '@app/stores/auth.store';
import { GenericStore } from '@app/stores/generic.store';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { getNewFileFormGroup, isFormValid } from 'src/app/+helpers/form.helper';
import {
  ApprovalRepresentationTypes,
  FileType,
  RepresentationTypes,
} from 'src/app/+models/shared.enums';
import { termsAndConditions } from 'src/app/+store/projects/constants';
import { DocumentUploadFormComponent } from '../../components/registration/document-upload-form/document-upload-form.component';
import { GeneralTermsAndConditionsStepFormComponent } from '../../components/registration/general-terms-and-conditions-step-form/general-terms-and-conditions-step-form.component';
import { PersonalDataStepFormComponent } from '../../components/registration/personal-data-step-form/personal-data-step-form.component';
import { ProofOfProfessionalStatusFormComponent } from '../../components/registration/proof-of-professional-status-form/proof-of-professional-status-form.component';
import { RepresentationTypeDialogComponent } from '../../components/registration/representation-type-dialog/representation-type-dialog.component';
import { VeriffVerificationWaitingDialogComponent } from '../../components/registration/veriff-verification-waiting-dialog/veriff-verification-waiting-dialog.component';
import { WizardStepComponent } from '../../components/wizard/wizard-step/wizard-step.component';
import {
  StepType,
  WizardComponent,
  WizardStep,
} from '../../components/wizard/wizard.component';
import {
  getPersonalDataFormGroup,
  getTemrsAndContitionsFormGroup,
  TermsAndConditionsFormGroup,
} from './registration-wizard-form.helper';

enum RegistrationStepType {
  termsAndConditions,
  personalData,
  legalPersonData,
  proofOfProfessionalStatus,
  documentUpload,
}

@Component({
  selector: 'app-registration-wizard',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    WizardComponent,
    WizardStepComponent,
    GeneralTermsAndConditionsStepFormComponent,
    PersonalDataStepFormComponent,
    DocumentUploadFormComponent,
    ProofOfProfessionalStatusFormComponent,
    LegalPersonValidatedFormComponent,
  ],
  templateUrl: './registration-wizard.component.html',
  styleUrl: './registration-wizard.component.scss',
})
export class RegistrationWizardComponent implements OnInit {
  private acceptTranslation = 'registrationWizard.iAccept';
  private declineTranslation = 'registrationWizard.decline';
  private perviousBtnTranslation = 'general.previous';
  private nextBtnTranslation = 'general.next';
  private finishBtnTranslation = 'registrationWizard.finishRegistration';

  private defaultWizardSteps: WizardStep<RegistrationStepType>[] = [
    {
      icon: '1',
      title: 'Általános szerződési feltételek',
      isActive: false,
      stepType: StepType.done,
      sectionType: RegistrationStepType.termsAndConditions,
    },
    {
      icon: '2',
      title: 'Adatok',
      isActive: false,
      stepType: StepType.done,
      sectionType: RegistrationStepType.personalData,
    },
  ];

  private additionalWizardSteps: WizardStep<RegistrationStepType>[] = [
    {
      icon: '3',
      title: 'Jogi személy adatai',
      isActive: true,
      stepType: StepType.available,
      sectionType: RegistrationStepType.legalPersonData,
    },
    {
      icon: '4',
      title: 'Dokumentumok feltöltése',
      isActive: false,
      stepType: StepType.available,
      sectionType: RegistrationStepType.documentUpload,
    },
  ];

  public items = signal<WizardStep<RegistrationStepType>[]>(
    this.defaultWizardSteps,
  );

  termsAndConditions: FormGroup<TermsAndConditionsFormGroup>;
  personalData: FormGroup<PersonalDataFormGroup>;
  legalPersonForm: FormGroup<LegalEntityFormGroup>;
  documentUpload: FormGroup<DocumentUploadFormGroup>;
  proofOfProfessionalStatus: FormGroup<FileUploadControl>;

  contractText = termsAndConditions; //TODO: load from API

  protected stepIndex = signal<number>(1);
  protected prevButtonText = signal<string>(this.declineTranslation);
  protected nextButtonText = signal<string>(this.acceptTranslation);

  protected prevButtonHasChevron = signal<boolean>(false);
  protected nextButtonHasChevron = signal<boolean>(false);

  protected authStore = inject(AuthStore);
  protected genericStore = inject(GenericStore);

  protected legalPersonService = inject(LegalPersonService);

  private readonly route = inject(ActivatedRoute);
  private readonly veriffService = inject(VeriffService);

  private legalEntityId = signal<string | undefined>(undefined);

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private router: Router,
    private accountService: AccountService,
  ) {
    this.termsAndConditions = getTemrsAndContitionsFormGroup(
      fb,
      this.authStore.user()?.termAccepted ?? false,
    );

    this.personalData = getPersonalDataFormGroup(fb);
    this.legalPersonForm = getLegalEntitiyData(this.fb);
    this.documentUpload = getDocumentUploadFormGroup(fb);
    this.proofOfProfessionalStatus = getNewFileFormGroup(
      this.fb,
      FileType.userLegalEntityRightDocuProfessional,
    );

    if (this.authStore.user()?.twoFactorEnabled == false) {
      this.router.navigate(['/auth/2fa/enable'], {
        queryParams: { returnUrl: router.url },
      });
    }

    if (this.authStore.user()?.termAccepted) {
      this.termsAndConditions.disable();
    }

    this.items.set(
      this.authStore.user()?.termAccepted
        ? [
            {
              icon: '1',
              title: 'Általános szerződési feltételek',
              isActive: true,
              stepType: StepType.done,
              sectionType: RegistrationStepType.termsAndConditions,
            },
            {
              icon: '2',
              title: 'general.data',
              isActive: false,
              stepType: StepType.available,
              sectionType: RegistrationStepType.personalData,
            },
          ]
        : [
            {
              icon: '1',
              title: 'Általános szerződési feltételek',
              isActive: true,
              stepType: StepType.available,
              sectionType: RegistrationStepType.termsAndConditions,
            },
            {
              icon: '2',
              title: 'general.data',
              isActive: false,
              stepType: StepType.available,
              sectionType: RegistrationStepType.personalData,
            },
          ],
    );

    effect(
      () => {
        if (this.stepIndex() == 1) {
          this.prevButtonHasChevron.set(false);
          this.nextButtonHasChevron.set(false);
          this.prevButtonText.set(this.declineTranslation);
          this.nextButtonText.set(this.acceptTranslation);
        }
        if (this.stepIndex() == 2) {
          this.setPrevNextBtn();
        }
        if (this.stepIndex() == 3) {
          this.setPrevNextBtn();
        }
        if (this.stepIndex() == 4) {
          if (this.items().length === 4) {
            this.lastBtnIsFinish();
          } else {
            this.setPrevNextBtn();
          }
        }
        if (this.stepIndex() == 5) {
          this.lastBtnIsFinish();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    if (this.route.snapshot.queryParamMap.get('veriffReturn') !== '1') {
      return;
    }
    this.stripVeriffReturnFromUrl();
    if (this.authStore.user()?.verified) {
      this.openRepresentationTypeModal();
      return;
    }
    this.openVeriffWaitingModal();
  }

  private lastBtnIsFinish(): void {
    this.prevButtonHasChevron.set(true);
    this.nextButtonHasChevron.set(false);
    this.prevButtonText.set(this.perviousBtnTranslation);
    this.nextButtonText.set(this.finishBtnTranslation);
  }

  private setPrevNextBtn(): void {
    this.prevButtonHasChevron.set(true);
    this.nextButtonHasChevron.set(true);
    this.prevButtonText.set(this.perviousBtnTranslation);
    this.nextButtonText.set(this.nextBtnTranslation);
  }

  protected onStepSelection(index: number) {
    this.stepIndex.set(index + 1);
  }

  private setActive(): void {
    this.items.update(() =>
      this.items().map((item, itemIndex) => ({
        ...item,
        isActive: itemIndex + 1 === this.stepIndex(),
      })),
    );
  }

  protected onPreviousButtonClick(): void {
    if (this.stepIndex() === 1) {
      return;
    }
    this.stepIndex.set(this.stepIndex() - 1);
    this.setActive();
  }

  private handleNextOnFirstStep(): void {
    if (
      !this.termsAndConditions.disabled &&
      !isFormValid(this.termsAndConditions)
    ) {
      this.items.update(() =>
        this.items().map((item, itemIndex) => ({
          ...item,
          stepType: itemIndex === 0 ? StepType.hasWarrning : item.stepType,
        })),
      );
      return;
    }

    if (this.termsAndConditions.disabled) {
      this.stepIndex.set(2);
      this.items.set([
        {
          icon: '1',
          title: 'Általános szerződési feltételek',
          isActive: false,
          stepType: StepType.done,
          sectionType: RegistrationStepType.termsAndConditions,
        },
        {
          icon: '2',
          title: 'Adatok',
          isActive: true,
          stepType: StepType.available,
          sectionType: RegistrationStepType.personalData,
        },
      ]);
    } else {
      this.authStore
        .setGeneralTermsAndConditions({
          acceptTermsAndConditions: true,
        })
        .subscribe((response) => {
          this.termsAndConditions.disable();

          this.stepIndex.set(2);
          this.items.set([
            {
              icon: '1',
              title: 'Általános szerződési feltételek',
              isActive: false,
              stepType: StepType.done,
              sectionType: RegistrationStepType.termsAndConditions,
            },
            {
              icon: '2',
              title: 'Adatok',
              isActive: true,
              stepType: StepType.available,
              sectionType: RegistrationStepType.personalData,
            },
          ]);
        });
    }
  }

  private legalPersonSelected(): void {
    this.stepIndex.set(3);
    this.items.set([
      ...this.defaultWizardSteps.map((wizardStep) => ({
        ...wizardStep,
        isActive: false,
        stepType: StepType.done,
      })),
      ...this.additionalWizardSteps,
    ]);
    this.setActive();
  }

  private professionalClientSelected(): void {
    this.stepIndex.set(3);
    this.items.set([
      ...this.defaultWizardSteps.map((wizardStep) => ({
        ...wizardStep,
        isActive: false,
        stepType: StepType.done,
        sectionType: RegistrationStepType.legalPersonData,
      })),
      ...this.additionalWizardSteps,
      {
        icon: '5',
        title: 'Szakamai jogállás igazolása',
        isActive: false,
        stepType: StepType.available,
        sectionType: RegistrationStepType.proofOfProfessionalStatus,
      },
    ]);
  }

  private handleNextOnSecondStep(): void {
    if (
      !this.termsAndConditions.disabled &&
      !isFormValid(this.termsAndConditions)
    ) {
      this.items.update(() =>
        this.items().map((item, itemIndex) => ({
          ...item,
          stepType: itemIndex <= 1 ? StepType.hasWarrning : item.stepType,
        })),
      );
      return;
    }
    if (!isFormValid(this.personalData)) {
      this.items.update(() =>
        this.items().map((item, itemIndex) => ({
          ...item,
          stepType: itemIndex === 1 ? StepType.hasWarrning : item.stepType,
        })),
      );
      return;
    }

    this.accountService
      .setPersonalData({
        titleCode: this.personalData.controls.title.value,
        firstName: this.personalData.controls.givenName.value,
        midName: this.personalData.controls.middleName.value,
        lastName: this.personalData.controls.familyName.value,
        citizenship: this.personalData.controls.citizenship.value,
        birthPlace: this.personalData.controls.birthPlace.value,
        birthDate: this.personalData.controls.dateOfBirth.value.toString(),
        email: this.authStore.user()?.email ?? '',
        motherFirstName: this.personalData.controls.motherFirstName.value,
        motherMidName: this.personalData.controls.motherMiddleName.value,
        motherLastName: this.personalData.controls.motherLastName.value,
        maidenFirstName: this.personalData.controls.birthFirstName.value,
        maidenMidName: this.personalData.controls.birthMiddleName.value,
        maidenLastName: this.personalData.controls.birthLastName.value,
        idCardType: Number(this.personalData.controls.certificateType.value),
        idCardNumber: this.personalData.controls.numberOfCertificate.value!,
        idCardNumberExpiry:
          this.personalData.controls.expiryDate.value.toString(),
        taxIdentifier: this.personalData.controls.optionalTaxNumber.value,
        bankAccountNumber: this.personalData.controls.optionalBankNumber.value,
        address: {
          countryCode: this.personalData.controls.country.value,
          zipCode: this.personalData.controls.postalCode.value,
          city: this.personalData.controls.city.value,
          publicSpace: this.personalData.controls.publicSpace.value,
          publicSpaceType: this.personalData.controls.publicSpaceType.value!,
          houseNumber: this.personalData.controls.houseNumber.value,
          doorOrFloor: this.personalData.controls.doorOrFloor.value,
        },
      })
      .subscribe(() => {
        this.proceedAfterPersonalDataSaved();
      });
  }

  private buildVeriffRedirectUrl(): string {
    const tree = this.router.createUrlTree(['/auth/registration/wizard'], {
      queryParams: { veriffReturn: '1' },
    });
    const path = this.router.serializeUrl(tree);
    return new URL(path, window.location.origin).href;
  }

  private stripVeriffReturnFromUrl(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { veriffReturn: null },
      queryParamsHandling: '',
      replaceUrl: true,
    });
  }

  private openRepresentationTypeModal(): void {
    const modalRef = this.modalService.open(RepresentationTypeDialogComponent, {
      centered: true,
    });

    modalRef.result.then(
      (result: RepresentationTypes) => {
        switch (result) {
          case RepresentationTypes.LEGAL_PERSON:
            this.legalPersonSelected();
            break;
          case RepresentationTypes.PROFESSIONAL_CLIENT:
            this.professionalClientSelected();
            break;
          case RepresentationTypes.NATURAL_PERSON:
            this.router.navigateByUrl('/investor-qualification-test');
            break;
        }
      },
      (reason) => console.log('Dismissed with:', reason),
    );
  }

  private openVeriffWaitingModal(): void {
    const modalRef = this.modalService.open(
      VeriffVerificationWaitingDialogComponent,
      {
        centered: true,
        backdrop: 'static',
        keyboard: false,
      },
    );

    modalRef.result.then(
      () => {
        this.authStore.refreshUserProfile().subscribe({
          next: () => this.openRepresentationTypeModal(),
          error: () => this.openRepresentationTypeModal(),
        });
      },
      () => {},
    );
  }

  private proceedAfterPersonalDataSaved(): void {
    if (this.authStore.user()?.verified) {
      this.openRepresentationTypeModal();
      return;
    }
    if (this.route.snapshot.queryParamMap.get('veriffReturn') === '1') {
      this.stripVeriffReturnFromUrl();
      this.openVeriffWaitingModal();
      return;
    }
    this.veriffService.createSession(this.buildVeriffRedirectUrl()).subscribe({
      next: (res) => {
        window.location.href = res.sessionUrl;
      },
      error: (err) => console.error('Veriff session failed', err),
    });
  }

  private handleNextOnThird(): void {
    if (!this.legalPersonForm.disabled && !isFormValid(this.legalPersonForm)) {
      return;
    }

    const formValues = this.legalPersonForm.getRawValue();

    this.legalPersonService
      .createLegalEntity({
        name: formValues.name,
        taxNumber: formValues.taxNumber,
        email: formValues.email,
        country: formValues.country,
        bankAccountNumber: formValues.bankAccountNumber,
        address: {
          zipCode: formValues.address.zipCode,
          city: formValues.address.city,
          publicSpace: formValues.address.publicSpace,
          publicSpaceType: formValues.address.publicSpaceType,
          houseNumber: formValues.address.houseNumber,
          doorOrFloor: formValues.address.doorOrFloor,
          countryCode: formValues.address.countryCode,
        },
      })
      .subscribe((response) => {
        this.legalEntityId.set(response.id);

        this.stepIndex.set(4);
        this.setActive();
      });
  }

  private handleNextOnFourth(): void {
    if (!this.documentUpload.disabled && !isFormValid(this.documentUpload)) {
      this.items.update(() =>
        this.items().map((item, itemIndex) => ({
          ...item,
          stepType: itemIndex === 3 ? StepType.hasWarrning : item.stepType,
        })),
      );
      return;
    }
    if (this.items().length > 4) {
      this.items.update(() =>
        this.items().map((item, itemIndex) => ({
          ...item,
          stepType:
            itemIndex === 4
              ? StepType.available
              : itemIndex === 3
                ? StepType.done
                : item.stepType,
        })),
      );
      this.stepIndex.set(5);
      this.setActive();
      return;
    }
    const documentUploadRaw = this.documentUpload.getRawValue();
    const files = [
      {
        uploadedFile: documentUploadRaw.signature.file,
        uploadedFileDescription: documentUploadRaw.signature.description,
        uploadedFileIsPublic: false,
        uploadedFileType:
          FileType.userLegalEntityRightDocuAuthorizedRepresentativeSignatureSpecimen,
      } as FileData,
      {
        uploadedFile: documentUploadRaw.authPartner.file,
        uploadedFileDescription: documentUploadRaw.authPartner.description,
        uploadedFileIsPublic: false,
        uploadedFileType: FileType.userLegalEntityRightDocuPoAAuthorizedPartner,
      } as FileData,
      {
        uploadedFile: documentUploadRaw.authRepresentative.file,
        uploadedFileDescription:
          documentUploadRaw.authRepresentative.description,
        uploadedFileIsPublic: false,
        uploadedFileType:
          FileType.userLegalEntityRightDocuPoAAuthorizedRepresentative,
      } as FileData,
    ];
    const legalEntityId = this.legalEntityId();
    const userId = this.authStore.user()?.id;

    if (legalEntityId && userId) {
      this.legalPersonService
        .postApprovalRequest(legalEntityId, {
          files: files,
          userId: userId,
          represents: ApprovalRepresentationTypes.LEGAL_PERSON,
        })
        .subscribe((response) => {
          this.router.navigate(['/profile']);
        });
    }
  }

  private handleNextOnFifth(): void {
    if (
      !this.proofOfProfessionalStatus.disabled &&
      !isFormValid(this.proofOfProfessionalStatus)
    ) {
      this.items.update(() =>
        this.items().map((item, itemIndex) => ({
          ...item,
          stepType: itemIndex === 4 ? StepType.hasWarrning : item.stepType,
        })),
      );
      return;
    }
    const documentUploadRaw = this.documentUpload.getRawValue();
    const professionalRaw = this.proofOfProfessionalStatus.getRawValue();

    const files = [
      {
        uploadedFile: documentUploadRaw.signature.file,
        uploadedFileDescription: documentUploadRaw.signature.description,
        uploadedFileIsPublic: false,
        uploadedFileType:
          FileType.userLegalEntityRightDocuAuthorizedRepresentativeSignatureSpecimen,
      } as FileData,
      {
        uploadedFile: documentUploadRaw.authPartner.file,
        uploadedFileDescription: documentUploadRaw.authPartner.description,
        uploadedFileIsPublic: false,
        uploadedFileType: FileType.userLegalEntityRightDocuPoAAuthorizedPartner,
      } as FileData,
      {
        uploadedFile: documentUploadRaw.authRepresentative.file,
        uploadedFileDescription:
          documentUploadRaw.authRepresentative.description,
        uploadedFileIsPublic: false,
        uploadedFileType:
          FileType.userLegalEntityRightDocuPoAAuthorizedRepresentative,
      } as FileData,
      {
        uploadedFile: professionalRaw.file,
        uploadedFileDescription: professionalRaw.description,
        uploadedFileIsPublic: false,
        uploadedFileType: FileType.userLegalEntityRightDocuProfessional,
      } as FileData,
    ];
    const legalEntityId = this.legalEntityId();
    const userId = this.authStore.user()?.id;

    if (legalEntityId && userId) {
      this.legalPersonService
        .postApprovalRequest(legalEntityId, {
          files: files,
          userId: userId,
          represents: ApprovalRepresentationTypes.PROFESSIONAL_CLIENT,
        })
        .subscribe((response) => {
          this.router.navigate(['/profile']);
        });
    }
  }

  protected onNextButtonClick() {
    switch (this.stepIndex()) {
      case 1:
        this.handleNextOnFirstStep();
        break;
      case 2:
        this.handleNextOnSecondStep();
        break;
      case 3:
        this.handleNextOnThird();
        break;
      case 4:
        this.handleNextOnFourth();
        break;
      case 5:
        this.handleNextOnFifth();
        break;
    }
  }
}
